import { useEffect, useState } from "react";
import { useList, useCreate, useUpdate } from "@/hooks/crud";
import { TextField, TextAreaField, SelectField } from "@/components/Fields";
import { StatusBadge } from "@/components/Badges";
import { ErrorBanner } from "@/components/Feedback";
import { RELEASE_STATUSES } from "@/lib/enums";
import type { TrackRelease } from "@/types/database";

const numOrNull = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function ReleasePanel({ trackId }: { trackId: number }) {
  const { data } = useList("track_release", `track_id=eq.${trackId}`);
  const release = (data?.rows?.[0] ?? null) as TrackRelease | null;

  const create = useCreate("track_release");
  const update = useUpdate("track_release");

  const [status, setStatus] = useState("PENDING");
  const [scUrl, setScUrl] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [allStreaming, setAllStreaming] = useState(false);
  const [distrokid, setDistrokid] = useState("");
  const [chartPos, setChartPos] = useState("");
  const [chartName, setChartName] = useState("");
  const [frRadio, setFrRadio] = useState("");
  const [epPlaylist, setEpPlaylist] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    setStatus(release?.status ?? "PENDING");
    setScUrl(release?.sc_url ?? "");
    setReleaseDate(release?.release_date ?? "");
    setAllStreaming(release?.all_streaming ?? false);
    setDistrokid(release?.distrokid_status ?? "");
    setChartPos(release?.groover_chart_position != null ? String(release.groover_chart_position) : "");
    setChartName(release?.groover_chart_name ?? "");
    setFrRadio(release?.fr_radio ?? "");
    setEpPlaylist(release?.ep_playlist_url ?? "");
    setNotes(release?.notes ?? "");
  }, [release]);

  const mutation = release ? update : create;

  async function save() {
    const body = {
      track_id: trackId,
      status,
      sc_url: scUrl.trim() || null,
      release_date: releaseDate.trim() || null,
      all_streaming: allStreaming,
      distrokid_status: distrokid.trim() || null,
      groover_chart_position: numOrNull(chartPos),
      groover_chart_name: chartName.trim() || null,
      fr_radio: frRadio.trim() || null,
      ep_playlist_url: epPlaylist.trim() || null,
      notes: notes.trim() || null,
    };
    if (release) await update.mutateAsync({ id: release.id, body });
    else await create.mutateAsync(body);
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>
          Release
          {release && <StatusBadge status={release.status} />}
        </h3>
      </div>
      <div className="panel-body">
        {mutation.error && <ErrorBanner error={mutation.error} />}
        <div className="field-row">
          <SelectField
            label="Status"
            value={status}
            onChange={setStatus}
            options={RELEASE_STATUSES}
          />
          <TextField
            label="Release date"
            value={releaseDate}
            onChange={setReleaseDate}
            type="date"
          />
        </div>
        <TextField label="SoundCloud URL" value={scUrl} onChange={setScUrl} />
        <div className="field">
          <label
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={allStreaming}
              onChange={(e) => setAllStreaming(e.target.checked)}
              style={{ width: "auto" }}
            />
            On all streaming platforms
          </label>
        </div>
        <div className="field-row">
          <TextField label="DistroKid status" value={distrokid} onChange={setDistrokid} />
          <TextField label="FR radio" value={frRadio} onChange={setFrRadio} />
        </div>
        <div className="field-row">
          <TextField
            label="Groover chart position"
            value={chartPos}
            onChange={setChartPos}
          />
          <TextField label="Groover chart name" value={chartName} onChange={setChartName} />
        </div>
        <TextField label="EP playlist URL" value={epPlaylist} onChange={setEpPlaylist} />
        <TextAreaField label="Notes" value={notes} onChange={setNotes} rows={2} />
        <div className="row-actions">
          <button
            className="btn btn-primary btn-sm"
            onClick={save}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "Saving…" : release ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
